/**
 * ConsentGateHost (web) — single app-root mount of the legal-consent
 * gate. Twin of mobile's ConsentGate.
 *
 * Reads users.me() and, whenever the user must (re-)accept the Privacy
 * Policy and/or Terms (after an admin clears consent following a policy
 * update), pops a NON-DISMISSABLE modal that blocks the app until they
 * agree. acceptConsent() stamps the acceptance; me() re-fetches
 * reactively and the gate closes on its own.
 *
 * Only active for signed-in users — anonymous visitors (public profile
 * pages) are never gated.
 */
import { useEffect, useState } from 'react';
import { useConvexAuth, useMutation, useQuery } from 'convex/react';

import { api as convexApi } from '../../../convex/functions/_generated/api';

export function ConsentGateHost() {
  const { isAuthenticated } = useConvexAuth();
  const me = useQuery(convexApi.users.me, isAuthenticated ? {} : 'skip');
  const acceptConsent = useMutation(convexApi.consent.acceptConsent);

  const needsPrivacy = me?.consent?.needsPrivacy ?? false;
  const needsTerms = me?.consent?.needsTerms ?? false;
  const open = Boolean(me && me.consent?.needsConsent);

  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset local checkbox state each time the gate (re)opens.
  useEffect(() => {
    if (open) {
      setAgreePrivacy(false);
      setAgreeTerms(false);
      setErr(null);
    }
  }, [open]);

  if (!open) return null;

  const ready =
    (!needsPrivacy || agreePrivacy) && (!needsTerms || agreeTerms);

  const submit = async () => {
    if (!ready || submitting) return;
    setSubmitting(true);
    setErr(null);
    try {
      await acceptConsent({
        privacy: needsPrivacy || undefined,
        terms: needsTerms || undefined,
      });
      // me() re-fetches reactively → needsConsent flips false → unmounts.
    } catch {
      setErr('Could not save your agreement. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true" style={overlay}>
      <div style={card}>
        <h2 className="serif" style={title}>We've updated our policies</h2>
        <p style={body}>
          Before you continue, please review and accept our latest{' '}
          {needsPrivacy && (
            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={link}>
              Privacy Policy
            </a>
          )}
          {needsPrivacy && needsTerms ? ' and ' : ''}
          {needsTerms && (
            <a href="/terms" target="_blank" rel="noopener noreferrer" style={link}>
              Terms &amp; Conditions
            </a>
          )}
          .
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
          {needsPrivacy && (
            <label style={checkRow}>
              <input
                type="checkbox"
                checked={agreePrivacy}
                onChange={(e) => setAgreePrivacy(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              <span style={checkText}>
                I have read and agree to the{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" style={link}>
                  Privacy Policy
                </a>.
              </span>
            </label>
          )}
          {needsTerms && (
            <label style={checkRow}>
              <input
                type="checkbox"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              <span style={checkText}>
                I have read and agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" style={link}>
                  Terms &amp; Conditions
                </a>.
              </span>
            </label>
          )}
        </div>

        {err && <p style={errText}>{err}</p>}

        <button
          type="button"
          onClick={() => void submit()}
          disabled={!ready || submitting}
          style={{ ...cta, opacity: ready && !submitting ? 1 : 0.5, cursor: ready && !submitting ? 'pointer' : 'not-allowed' }}
        >
          {submitting ? 'Saving…' : 'Agree & continue'}
        </button>
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 2000,
  background: 'rgba(0,0,0,0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
};
const card: React.CSSProperties = {
  width: '100%',
  maxWidth: 460,
  background: 'var(--surface)',
  border: '1px solid var(--line-strong)',
  borderRadius: 18,
  padding: 28,
  boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
};
const title: React.CSSProperties = { fontSize: 24, fontWeight: 500, marginBottom: 10 };
const body: React.CSSProperties = { fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.6 };
const checkRow: React.CSSProperties = { display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' };
const checkText: React.CSSProperties = { fontSize: 14, color: 'var(--ink)', lineHeight: 1.5 };
const link: React.CSSProperties = { color: 'var(--coral)', textDecoration: 'underline', fontWeight: 600 };
const errText: React.CSSProperties = { marginTop: 12, fontSize: 13, color: '#a8331f' };
const cta: React.CSSProperties = {
  marginTop: 22,
  width: '100%',
  padding: '13px 16px',
  background: 'var(--ink)',
  color: 'var(--bg)',
  border: 'none',
  borderRadius: 100,
  fontSize: 15,
  fontWeight: 600,
  fontFamily: 'var(--font-ui)',
};
